import { err, ok, Result } from 'neverthrow';
import { env } from 'next-runtime-env';

import { BffAppError, BffError } from '@/server/errors';

const moduleName = 'server/utils/getEnvironmentVars';

const isClientSide = () => typeof window !== 'undefined';

/**
 * Retrieves environment variables, but will return an Error Result if any of
 * them are `undefined`
 *
 * Example:
 * ```
 * getEnvironmentVariables(['NODE_ENV', 'FOOBAR_HOSTNAME'])
 *  .andThen(envVars => {
 *    console.log(envVars);
 *    // { NODE_ENV: 'production', FOOBAR_HOSTNAME: 'example.com' }
 *  });
 * ```
 *
 * The Result will contain `BffAppError.MissingEnvironmentVariables` if any of the
 * environment variables provided are undefined.
 *
 * The implementation is isomorphic, so calling this function server-side will
 * load environment variables from `process.env`, whereas calling it client-side
 * will load them using `@omers-packages/next-isomorphic-runtime-env`
 *
 * @param varNames Names of the environment variables to get the values of
 * @param options.warnOnMissing Logs (warning) for missing environment variables
 * @returns A record containing the environment variable names and their corresponding values
 */
const getEnvironmentVariables = <T extends string>(varNames: Array<T>): Result<Record<T, string>, BffError> => {
  // Get an array of all environment variable names that are not defined
  const undefinedEnvVars = varNames.filter((name) => env(name) === undefined);

  // If that array isn't empty, return a BffError indicating that some
  // environment variables are undefined
  if (undefinedEnvVars.length > 0) {
    return err({
      code: BffAppError.MissingEnvironmentVariables,
      cause: `The following ${
        isClientSide() ? 'client' : 'server'
      }-side environment variables are undefined: ${undefinedEnvVars.join(', ')}`,
      moduleName
    });
  }

  // Otherwise, return a record containing environment variables and their
  // corresponding values
  const entries = varNames.map((name) => [name, env(name) as string]);
  const envVarRecord: Record<T, string> = Object.fromEntries(entries);

  return ok(envVarRecord);
};

export default getEnvironmentVariables;
